/**
 * Categorize Time groups — uncategorized activity clusters awaiting
 * client/project assignment. Each group has an app icon, event count,
 * hours, and a date range string.
 *
 * Data modelled after the CategorizeTime skill-test prototype.
 */

import type { AppKey } from '@/types/appIcon';

export interface CategorizeGroupEvent {
  id: string;
  timestamp: string;
  windowTitle: string;
  duration: string;
}

export interface CategorizeGroup {
  id: string;
  appKey: AppKey;
  title: string;
  hours: number;
  eventCount: number;
  dateRange: string;
  events: readonly CategorizeGroupEvent[];
}

export const CATEGORIZE_GROUPS: readonly CategorizeGroup[] = [
  {
    id: 'g1',
    title: 'Microsoft Word \u2014 Document1',
    appKey: 'word',
    eventCount: 18,
    hours: 2.4,
    dateRange: 'Apr 7 \u2013 Apr 11',
    events: [
      {
        id: 'e1',
        timestamp: 'Apr 7, 9:02 AM',
        windowTitle: 'Document1 \u2014 Word',
        duration: '0.3h',
      },
      {
        id: 'e2',
        timestamp: 'Apr 7, 10:41 AM',
        windowTitle: 'Document1 \u2014 Word',
        duration: '0.2h',
      },
      {
        id: 'e3',
        timestamp: 'Apr 8, 2:15 PM',
        windowTitle: 'Document1 \u2014 Word',
        duration: '0.4h',
      },
      {
        id: 'e4',
        timestamp: 'Apr 9, 11:08 AM',
        windowTitle: 'Document1 \u2014 Word',
        duration: '0.5h',
      },
      {
        id: 'e5',
        timestamp: 'Apr 11, 3:30 PM',
        windowTitle: 'Document1 \u2014 Word',
        duration: '1.0h',
      },
    ],
  },
  {
    id: 'g2',
    title: 'Chrome \u2014 Untitled',
    appKey: 'chrome',
    eventCount: 9,
    hours: 1.8,
    dateRange: 'Apr 7 \u2013 Apr 11',
    events: [
      {
        id: 'e6',
        timestamp: 'Apr 7, 9:30 AM',
        windowTitle: 'Untitled \u2014 Chrome',
        duration: '0.4h',
      },
      {
        id: 'e7',
        timestamp: 'Apr 8, 11:15 AM',
        windowTitle: 'Untitled \u2014 Chrome',
        duration: '0.3h',
      },
      {
        id: 'e8',
        timestamp: 'Apr 9, 2:00 PM',
        windowTitle: 'Untitled \u2014 Chrome',
        duration: '0.5h',
      },
      {
        id: 'e9',
        timestamp: 'Apr 10, 10:20 AM',
        windowTitle: 'Untitled \u2014 Chrome',
        duration: '0.4h',
      },
      {
        id: 'e10',
        timestamp: 'Apr 11, 1:45 PM',
        windowTitle: 'Untitled \u2014 Chrome',
        duration: '0.2h',
      },
    ],
  },
  {
    id: 'g3',
    title: 'VS Code \u2014 Untitled-1',
    appKey: 'vscode',
    eventCount: 7,
    hours: 1.4,
    dateRange: 'Apr 7 \u2013 Apr 10',
    events: [
      {
        id: 'e11',
        timestamp: 'Apr 7, 11:00 AM',
        windowTitle: 'Untitled-1 \u2014 VS Code',
        duration: '0.3h',
      },
      {
        id: 'e12',
        timestamp: 'Apr 8, 3:30 PM',
        windowTitle: 'Untitled-1 \u2014 VS Code',
        duration: '0.4h',
      },
      {
        id: 'e13',
        timestamp: 'Apr 9, 9:45 AM',
        windowTitle: 'Untitled-1 \u2014 VS Code',
        duration: '0.3h',
      },
      {
        id: 'e14',
        timestamp: 'Apr 10, 2:15 PM',
        windowTitle: 'Untitled-1 \u2014 VS Code',
        duration: '0.4h',
      },
    ],
  },
  {
    id: 'g4',
    title: 'Excel \u2014 Book1',
    appKey: 'excel',
    eventCount: 5,
    hours: 1.1,
    dateRange: 'Apr 8 \u2013 Apr 11',
    events: [
      {
        id: 'e15',
        timestamp: 'Apr 8, 10:00 AM',
        windowTitle: 'Book1 \u2014 Excel',
        duration: '0.3h',
      },
      {
        id: 'e16',
        timestamp: 'Apr 9, 1:30 PM',
        windowTitle: 'Book1 \u2014 Excel',
        duration: '0.2h',
      },
      {
        id: 'e17',
        timestamp: 'Apr 10, 4:00 PM',
        windowTitle: 'Book1 \u2014 Excel',
        duration: '0.3h',
      },
      {
        id: 'e18',
        timestamp: 'Apr 11, 9:15 AM',
        windowTitle: 'Book1 \u2014 Excel',
        duration: '0.3h',
      },
    ],
  },
  {
    id: 'g5',
    title: 'Outlook \u2014 (No Subject)',
    appKey: 'outlook',
    eventCount: 6,
    hours: 0.9,
    dateRange: 'Apr 7 \u2013 Apr 11',
    events: [
      {
        id: 'e19',
        timestamp: 'Apr 7, 8:30 AM',
        windowTitle: '(No Subject) \u2014 Outlook',
        duration: '0.2h',
      },
      {
        id: 'e20',
        timestamp: 'Apr 8, 12:00 PM',
        windowTitle: '(No Subject) \u2014 Outlook',
        duration: '0.1h',
      },
      {
        id: 'e21',
        timestamp: 'Apr 9, 3:45 PM',
        windowTitle: '(No Subject) \u2014 Outlook',
        duration: '0.2h',
      },
      {
        id: 'e22',
        timestamp: 'Apr 10, 9:00 AM',
        windowTitle: '(No Subject) \u2014 Outlook',
        duration: '0.2h',
      },
      {
        id: 'e23',
        timestamp: 'Apr 11, 11:30 AM',
        windowTitle: '(No Subject) \u2014 Outlook',
        duration: '0.2h',
      },
    ],
  },
  {
    id: 'g6',
    title: 'Adobe Acrobat \u2014 Untitled',
    appKey: 'unknown',
    eventCount: 4,
    hours: 0.7,
    dateRange: 'Apr 8 \u2013 Apr 10',
    events: [
      {
        id: 'e24',
        timestamp: 'Apr 8, 1:00 PM',
        windowTitle: 'Untitled \u2014 Acrobat',
        duration: '0.2h',
      },
      {
        id: 'e25',
        timestamp: 'Apr 9, 10:30 AM',
        windowTitle: 'Untitled \u2014 Acrobat',
        duration: '0.2h',
      },
      {
        id: 'e26',
        timestamp: 'Apr 10, 3:00 PM',
        windowTitle: 'Untitled \u2014 Acrobat',
        duration: '0.3h',
      },
    ],
  },
  {
    id: 'g7',
    title: 'Microsoft Teams \u2014 Meeting',
    appKey: 'teams',
    eventCount: 3,
    hours: 0.5,
    dateRange: 'Apr 9 \u2013 Apr 11',
    events: [
      {
        id: 'e27',
        timestamp: 'Apr 9, 2:00 PM',
        windowTitle: 'Meeting \u2014 Teams',
        duration: '0.2h',
      },
      {
        id: 'e28',
        timestamp: 'Apr 10, 11:00 AM',
        windowTitle: 'Meeting \u2014 Teams',
        duration: '0.2h',
      },
      {
        id: 'e29',
        timestamp: 'Apr 11, 4:00 PM',
        windowTitle: 'Meeting \u2014 Teams',
        duration: '0.1h',
      },
    ],
  },
  {
    id: 'g8',
    title: 'Notepad \u2014 Untitled',
    appKey: 'unknown',
    eventCount: 2,
    hours: 0.2,
    dateRange: 'Apr 10 \u2013 Apr 11',
    events: [
      {
        id: 'e30',
        timestamp: 'Apr 10, 5:00 PM',
        windowTitle: 'Untitled \u2014 Notepad',
        duration: '0.1h',
      },
      {
        id: 'e31',
        timestamp: 'Apr 11, 8:45 AM',
        windowTitle: 'Untitled \u2014 Notepad',
        duration: '0.1h',
      },
    ],
  },
] as const;

export const CATEGORIZE_CLIENTS = [
  'Meridian Capital',
  'TechVenture Labs',
  'Pinnacle Financial',
  'Internal / General',
] as const;

export const CATEGORIZE_PROJECTS = [
  'Corporate Restructuring',
  'Patent Portfolio Review',
  'Regulatory Compliance',
  'Internal Training',
  'Firm Administration',
] as const;

export const CATEGORIZE_TOTAL_HOURS = '8.4h';
